import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { UdeuRecord } from '../../../../model/classes/udeu-record';
import { DateType } from 'projects/alden-extension/src/lib/models/date-types';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CreateRecordDialogComponent } from '../../../records/create-record/create-record.dialog';
import { RecordActionTypes } from 'projects/deusto-extension/src/lib/store/actions/record-actions';
import { Store } from '@ngrx/store';
import { AppStore, SnackbarErrorAction } from '@alfresco/aca-shared/store';
import { AssignRegisterLoanAction } from 'projects/deusto-extension/src/lib/store/actions/loan-actions';
import { SMTConfirmDialogComponent } from 'projects/alden-extension/src/lib/dialogs/confirm/confirm.dialog';
import { UdeuLoan } from 'projects/deusto-extension/src/lib/model/classes/udeu-loan';
import { TranslationService } from '@alfresco/adf-core';
import { LoanService } from 'projects/deusto-extension/src/lib/services/loan.service';
import { KeyValue } from '@angular/common';
import { UDEU_LOAN_BORROWED_STATUS, UDEU_LOAN_IN_PROGRESS_STATUS } from 'projects/deusto-extension/src/lib/model/contants/deusto';
import { MIMETYPES } from 'projects/alden-extension/src/lib/models/mime-types';

@Component({
  selector: 'alden-registers',
  templateUrl: './registers.component.html',
  styleUrls: ['./registers.component.scss'],
})
export class RegistersComponent implements OnChanges {
  @Input() loan: UdeuLoan;
  @Input() group: FormGroup<any>;
  @Input() controlName: string;
  @Input() isManager: boolean;

  private _formBuilder = inject(FormBuilder);

  DateType: typeof DateType = DateType;
  disabled: boolean;
  canViewRecord: boolean;

  loading: boolean;

  loanStatus: string;
  UDEU_LOAN_IN_PROGRESS_STATUS = UDEU_LOAN_IN_PROGRESS_STATUS;

  constructor(private dialogRef: MatDialogRef<RegistersComponent>, private store: Store<AppStore>, public dialog: MatDialog, private loanService: LoanService, private translationService: TranslationService) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.disabled = this.group.get(this.controlName).disabled;
    this.loanStatus = (this.loan.status.status as KeyValue<string, string>).key;
    this.canViewRecord = this.isManager || this.loanStatus === UDEU_LOAN_BORROWED_STATUS;

    const loanChange = changes["loan"];
    if(loanChange !== undefined) {
      this.loan = loanChange.currentValue;
      const records = this.loan.lentRecords as UdeuRecord[];
      this.formArray.clear();
      if (records) {
        records.forEach((record) => {
          const form = this.createForm(record);
          this.formArray.push(form);
        });
      }
    }
  }

  createForm(record?: UdeuRecord): FormGroup<any> {
    return this._formBuilder.group({
      id: [{ value: record?.id, disabled: true }],
      name: [{ value: record?.name, disabled: true }],
      category: [{ value: record?.data?.category, disabled: true }],
      typology: [{ value: record?.data?.typology, disabled: true }],
      title: [{ value: record?.data?.title, disabled: true }],
      depositProv: [{ value: record?.data?.depositProv, disabled: true }],
      descLevel: [{ value: record?.data?.descLevel, disabled: true }],
      startDate: [{ value: record?.data?.startDate, disabled: true }],
      endDate: [{ value: record?.data?.endDate, disabled: true }],
      supportVolume: [{ value: record?.data?.supportVolume, disabled: true }],
    });
  }

  get formArray(): FormArray {
    return this.group.get(this.controlName) as FormArray;
  }

  formArrayGroup (index: number): FormGroup {
    return this.formArray.at(index) as FormGroup;
  }

  viewReg(id: string) {
    this.dialog.open(CreateRecordDialogComponent,
      {
        disableClose: true,
        autoFocus: true,
        width: '80%',
        maxHeight: '90%', 
        data:  { UUIDs: [id], recordActionType: RecordActionTypes.ViewRecord }
      },
    );
  }

  assignRegister() {
    const dialogRef = this.dialog.open(SMTConfirmDialogComponent, {
      data: {
        title: 'DEUSTO.NEW_MENU.MENU_ITEMS.LOANS.STEPS.REGISTERS.NEW_ITEM',
        message: 'Se moverá de modulo sin guardar los cambios. ¿Seguro que desea continuar?',
        noLabel: 'Cancelar',
        yesLabel: 'Aceptar'
      },
      minWidth: '250px'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {       
        this.store.dispatch(new AssignRegisterLoanAction());
        this.dialogRef.close();
      }
    });
  }

  delete(control: AbstractControl<any, any>, index: number) {
    const recordId = control.get('id').value;
    const recordName = control.get('name').value;
    const dialogRef = this.dialog.open(SMTConfirmDialogComponent, {
      data: { 
        title: 'DEUSTO.DIALOGS.REVOKE_LOAN.TITLE',
        htmlContent: this.translationService.instant('DEUSTO.DIALOGS.REVOKE_LOAN.MESSAGE', { regName: recordName, loanName: this.loan.name }),
        noLabel: 'DEUSTO.DIALOGS.REVOKE_LOAN.NO_LABEL',
        yesLabel: 'DEUSTO.DIALOGS.REVOKE_LOAN.YES_LABEL',
      },
      minWidth: '250px'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loading = true;
        const status = typeof this.loan.status.status === 'string' ? this.loan.status.status : this.loan.status.status.key;
        this.loanService.asssignRecord(this.loan.id, [recordId], true, null,status).then(() => {
          const formArray = this.formArray;
          formArray.removeAt(index);
          this.loading = false;
        });
      }
    });
  }

  async printReceipt(recordId: string) {
    this.loading = true;

    await this.loanService.getReceipt(this.loan.id, recordId).then((res) => {
      const filename = this.loan.name + "_Testigo" + MIMETYPES[res.type.split(';')[0]].extension;
      
      const file = new File([res], filename);
      const url= window.URL.createObjectURL(file);
      
      var linkToFile = document.createElement('a');
      linkToFile.download = filename;
      linkToFile.href = url;
      linkToFile.click();
    }).catch((err) => {
      console.error("ERROR", err);
      this.store.dispatch(new SnackbarErrorAction('ALDEN.MESSAGES.ERRORS.RESPONSE.500'));
    });

    this.loading = false;
  }

}
